import { useContext } from '@nuxtjs/composition-api';
import defaultConfig from '@shared/config';
import { ComponentConfig } from '@shared/types/ComponentConfig';
import { ComposableConfig } from '@shared/types/ComposableConfig';
import { GlobalConfig } from '@shared/types/GlobalConfig';
import mergeWith from 'lodash/mergeWith';

const mergeCustomizer = (_: any, srcValue: any) => {
  // Do not merge arrays, brand config overrides default config
  if (Array.isArray(srcValue)) return srcValue;
};

/**
 * Brand configuration interface that combines all configuration types
 * @interface BrandConfig
 */
interface BrandConfig {
  /** Composable-specific configuration merged with brand overrides */
  composableConfig: Required<ComposableConfig>;
  /** Component-specific configuration merged with brand overrides */
  componentConfig: Required<ComponentConfig>;
  /** Global application configuration merged with brand overrides */
  globalConfig: Required<GlobalConfig>;
}
/**
 * Composable for managing brand-specific configuration
 *
 * Merges default configuration with brand-specific overrides from the Nuxt context.
 * This allows different brands to customize composable, component, and global settings
 * while maintaining sensible defaults.
 *
 * @returns {BrandConfig} Object containing merged configuration for composables, components, and global settings
 *
 * @example
 * ```typescript
 * const { composableConfig, componentConfig, globalConfig } = useBrandConfig();
 *
 * // Access specific configuration
 * const deliveryConfig = component.ProductDeliveryEstimate;
 * const myFitmentConfig = component.useFitment;
 * const globalSettings = global.someGlobalSetting;
 * ```
 */
const useBrandConfig = (): BrandConfig => {
  const { $config } = useContext();

  const composableConfig = mergeWith(
    defaultConfig.composableConfig,
    $config.composableConfig,
    mergeCustomizer,
  );
  const componentConfig = mergeWith(
    defaultConfig.componentConfig,
    $config.componentConfig,
    mergeCustomizer,
  );
  const globalConfig = mergeWith(defaultConfig.globalConfig, $config.globalConfig, mergeCustomizer);

  return {
    composableConfig,
    componentConfig,
    globalConfig,
  };
};

export default useBrandConfig;
